// src/components/currencies/FormAddEditCurrencies.jsx

import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Stack, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import useCreateCurrencies from "../../hooks/currencies/useCreateCurrencies";
import useUpdateCurrencies from "../../hooks/currencies/useUpdateCurrencies";
import FormInput from "../inputs/FormInput";

const FormAddEditCurrencies = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [symbol, setSymbol] = useState("");
    const [exchangeRate, setExchangeRate] = useState("1");
    const [isDefault, setIsDefault] = useState("no");
    const [status, setStatus] = useState("active");

    const { createCurrencies, isPending: isCreating } = useCreateCurrencies();
    const { editCurrencies, isUpdate } = useUpdateCurrencies();

    const loadingSubmit = isCreating || isUpdate;

    useEffect(() => {
        if (isEdit && currentData) {
            setName(currentData?.name || "");
            setCode(currentData.code || "");
            setSymbol(currentData.symbol || "");
            setExchangeRate(currentData.exchange_rate || "1");
            setIsDefault(currentData.is_default ? "yes" : "no");
            setStatus(currentData?.status || "active");
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();
        const payload = {
            name,
            code: code.toUpperCase(),
            symbol,
            exchange_rate: parseFloat(exchangeRate) || 1,
            is_default: isDefault === "yes",
            status,
        };
        if (isEdit && currentData?.id) {
            editCurrencies(
                { id: currentData.id, req: payload },
                { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } }
            );
        } else {
            createCurrencies(payload, { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } });
        }
    };

    const statusOptions = [
        { id: "active", name: isArabic ? "نشط" : "Active" },
        { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
    ];

    const defaultOptions = [
        { id: "yes", name: isArabic ? "نعم" : "Yes" },
        { id: "no", name: isArabic ? "لا" : "No" },
    ];

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box
                component="form"
                onSubmit={SubmitForm}
                sx={{
                    p: { xs: 2, md: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">
                        {isEdit
                            ? isArabic
                                ? "تعديل عملة"
                                : "Edit currency"
                            : isArabic
                                ? "إضافة عملة جديدة"
                                : "Create new currency"}
                    </Typography>
                </Box>

                <Box mb={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "اسم العملة" : "Currency Name"}
                                placeholder={isArabic ? "أدخل اسم العملة" : "Enter currency name"}
                                type="text"
                                name="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "كود العملة" : "Currency code"}
                                placeholder={isArabic ? "مثال: USD" : "e.g. USD"}
                                type="text"
                                name="code"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "رمز العملة" : "Currency symbol"}
                                placeholder={isArabic ? "مثال: $" : "e.g. $"}
                                type="text"
                                name="symbol"
                                value={symbol}
                                onChange={(e) => setSymbol(e.target.value)}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "سعر الصرف" : "Exchange rate"}
                                placeholder={isArabic ? "مثال: 1.00" : "e.g. 1.00"}
                                type="number"
                                name="exchange_rate"
                                value={exchangeRate}
                                onChange={(e) => setExchangeRate(e.target.value)}
                                inputProps={{ step: "0.01", min: "0" }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الحالة" : "Status"}
                                type="select"
                                name="status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                options={statusOptions}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "تعيين كعملة افتراضية" : "Set as default currency"}
                                type="select"
                                name="is_default"
                                value={isDefault}
                                disabled={true}
                                onChange={(e) => setIsDefault(e.target.value)}
                                options={defaultOptions}
                                required={false}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Stack direction="row" spacing={1}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loadingSubmit}
                        >
                            {loadingSubmit
                                ? isArabic
                                    ? "جارٍ الحفظ..."
                                    : "Saving..."
                                : isEdit
                                    ? isArabic
                                        ? "تحديث"
                                        : "Update"
                                    : isArabic
                                        ? "حفظ"
                                        : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditCurrencies;
